import React, { useState, useCallback, useRef } from 'react';
import { colors } from '@/assets/styles/colors';
import { IconScout, type IconScoutName } from '@/components/ui/images/IconScout';
import { Text } from '@/components/ui/text/Text';


interface SpinnerButtonProps {
  icon: IconScoutName;
  onClick: () => void;
  disabled: boolean;
  borderColor: string;
  buttonColor: string;
  label: string;
  dataTestId?: string;
}

const SpinnerButton: React.FC<SpinnerButtonProps> = ({
  icon,
  onClick,
  disabled,
  borderColor,
  buttonColor,
  label,
  dataTestId,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    tabIndex={-1}
    className="
      flex items-center justify-center
      w-6 h-6
      border rounded-full
      transition-all duration-200
      outline-none
      disabled:opacity-40 disabled:cursor-not-allowed
    "
    style={{
      borderColor,
      backgroundColor: colors.grays.neutralFF,
      color: buttonColor,
    }}
    aria-label={label}
    data-testid={dataTestId}
  >
    <IconScout name={icon} size={12} />
  </button>
);

export interface NumericSpinnerProps {
  id?: string;
  label?: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  readOnly?: boolean;
  onChange?: (value: number) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  className?: string;
  inputClassName?: string;
  'data-testid'?: string;
  /**
   * Show increment/decrement buttons
   * @default true
   */
  showButtons?: boolean;
  /**
   * Allow keyboard arrow navigation
   * @default true
   */
  allowKeyboard?: boolean;
  /**
   * Pad value with leading zeros. Pass 0 to disable padding (e.g. the shift
   * "Semanas (cantidad)" spinner shows `1` rather than `01`).
   * @default 2
   */
  padLength?: number;
}


export const NumericSpinner: React.FC<NumericSpinnerProps> = ({
  id,
  label,
  value,
  min = 0,
  max = 99,
  step = 1,
  disabled = false,
  readOnly = false,
  onChange,
  onFocus,
  onBlur,
  className = '',
  inputClassName = '',
  'data-testid': dataTestId,
  showButtons = true,
  allowKeyboard = true,
  padLength = 2,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const inputId = id || `numeric-spinner-${Math.random().toString(36).substring(2, 9)}`;

  // Format value with padding
  const formatValue = useCallback(
    (val: number): string => val.toString().padStart(padLength, '0'),
    [padLength],
  );

  // Clamp value within min/max
  const clampValue = useCallback(
    (val: number): number => Math.max(min, Math.min(max, val)),
    [min, max],
  );

  // Handle increment
  const handleIncrement = useCallback(() => {
    if (disabled || readOnly) return;
    const newValue = clampValue(value + step);
    if (newValue !== value) {
      onChange?.(newValue);
    }
  }, [value, step, disabled, readOnly, clampValue, onChange]);

  // Handle decrement
  const handleDecrement = useCallback(() => {
    if (disabled || readOnly) return;
    const newValue = clampValue(value - step);
    if (newValue !== value) {
      onChange?.(newValue);
    }
  }, [value, step, disabled, readOnly, clampValue, onChange]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (!allowKeyboard || disabled || readOnly) return;

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        handleIncrement();
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        handleDecrement();
      }
    },
    [allowKeyboard, disabled, readOnly, handleIncrement, handleDecrement],
  );

  // Handle direct input change
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled || readOnly) return;

      const inputValue = event.target.value;
      
      // Allow empty for editing
      if (inputValue === '') {
        return;
      }

      // Parse and validate
      const parsed = parseInt(inputValue, 10);
      if (!isNaN(parsed)) {
        const clamped = clampValue(parsed);
        onChange?.(clamped);
      }
    },
    [disabled, readOnly, clampValue, onChange],
  );

  // Handle focus
  const handleFocus = useCallback(() => {
    setIsFocused(true);
    onFocus?.();
  }, [onFocus]);

  // Handle blur - ensure value is formatted
  const handleBlur = useCallback(() => {
    setIsFocused(false);
    onBlur?.();
    
    // Reset to clamped value on blur
    const clamped = clampValue(value);
    if (clamped !== value) {
      onChange?.(clamped);
    }
  }, [value, clampValue, onChange, onBlur]);

  // Colors following project patterns
  const borderColor = disabled
    ? colors.grays.neutralCC
    : isFocused
      ? colors.important.main
      : colors.grays.neutralE5;

  const textColor = disabled ? colors.grays.neutral99 : colors.grays.neutral33;
  const labelColor = disabled ? colors.grays.neutral99 : colors.grays.neutral66;
  const buttonColor = disabled ? colors.grays.neutralCC : colors.primary.main;
  
  return (
    <div className={`inline-flex flex-col ${className}`}>
      {/* Label */}
      {label && (
        <Text
          as="label"
          variant="subheader-medium"
          color={labelColor}
          className="transition-all duration-200 mb-1"
        >
          {label}
        </Text>
      )}

      {/* Input container */}
      <div
        className={`
          flex items-center gap-2
          ${disabled ? 'opacity-60 cursor-not-allowed' : ''}
        `}
      >
        {/* Decrement button */}
        {showButtons && (
          <SpinnerButton
            icon="minus"
            onClick={handleDecrement}
            disabled={disabled || readOnly || value <= min}
            borderColor={borderColor}
            buttonColor={buttonColor}
            label={`Decrement ${label || 'value'}`}
            dataTestId={dataTestId ? `${dataTestId}-decrement` : undefined}
          />
        )}

        {/* Input field */}
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          inputMode="numeric"
          value={formatValue(value)}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          readOnly={readOnly}
          className={`
            w-6
            h-6
            text-center
            text-sm
            font-medium
            bg-transparent
            transition-all duration-200
            outline-none
            ${showButtons ? 'border-0' : 'border rounded-lg'}
            ${inputClassName}
          `}
          style={{
            borderColor: showButtons ? undefined : borderColor,
            color: textColor,
          }}
          aria-label={label}
          data-testid={dataTestId}
        />

        {/* Increment button */}
        {showButtons && (
          <SpinnerButton
            icon="plus"
            onClick={handleIncrement}
            disabled={disabled || readOnly || value >= max}
            borderColor={borderColor}
            buttonColor={buttonColor}
            label={`Increment ${label || 'value'}`}
            dataTestId={dataTestId ? `${dataTestId}-increment` : undefined}
          />
        )}
      </div>
    </div>
  );
};
